import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { type DefendantFormSchema, defendantFormSchema } from "@/features/case-evaluation/types/defendant";
import { countriesQueryOptions } from "@/features/case-record/api/queries/country-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import cityService from "@/features/dashboard/api/services/city-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import userService from "@/features/dashboard/api/services/user-service";
import { useAuth } from "@/hooks/use-auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconCirclePlus, IconHelpCircle, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import Alert from "../core/alert";
import { Checkbox } from "../core/forms/checkbox";
import { CheckboxGroup } from "../core/forms/checkbox-group";
import Combobox from "../core/forms/combobox";
import Tooltip from "../core/tooltip";

export default function CaseDefendantModal({
	data,
	emails,
	isEdit,
	caseData,
	allParties,
	isPlaintiff = false,
	defendantLabel,
	onSubmit,
}: {
	data?: DefendantFormSchema;
	isEdit?: boolean;
	isPlaintiff?: boolean;
	caseData: CaseRecord;
	allParties: any;
	emails?: (string | undefined)[];
	onSubmit: (data: DefendantFormSchema) => void;
}) {
	const { closeModal } = useModalControl();
	const form = useForm<DefendantFormSchema>({
		resolver: zodResolver(defendantFormSchema),
		defaultValues: {
			first_name: data?.first_name || "",
			last_name: data?.last_name || "",
			middle_name: data?.middle_name || "",
			email: data?.email || "",
			mobile_number: data?.mobile_number || "",
			landline: data?.landline || "",
			classification: data?.classification || "Individual",
			corporation: data?.corporation || "",
			barangay: data?.barangay || "",
			address_1: data?.address_1 || "",
			address_2: data?.address_2 || "",
			zipcode: data?.zipcode || "",
			region_code: data?.region_code || "",
			region: data?.region || {},
			province_code: data?.province_code || "",
			province: data?.province || {},
			city_code: data?.city_code || "",
			city: data?.city || {},
			suffix: data?.suffix || "",
			country: data?.country || "",
			use_existing_party: false,
			address_type: "domestic",
			party_id: null,
			representatives: data?.representatives || [],
		},
	});

	const [existingParty, setExistingParty] = useState(false);
	const [debouncedEmail, setDebouncedEmail] = useState("");
	const [emailExists, setEmailExists] = useState(false);
	const [nameExists, setNameExists] = useState(false);
	const email = form.watch("email");
	const addressType = form.watch("address_type");

	const representatives = useFieldArray({
		control: form.control,
		name: "representatives",
	});

	const handleAddRow = () => {
		representatives.append({
			name: null,
			email: null,
		});
	};

	// const updateDebouncedEmail = useCallback(
	// 	debounce(
	// 		(newEmail) => {
	// 			setDebouncedEmail(newEmail);
	// 		},
	// 		500,
	// 		{ trailing: true },
	// 	),
	// 	[],
	// );

	// useEffect(() => {
	// 	updateDebouncedEmail(email);
	// }, [email, updateDebouncedEmail]);

	// useEffect(() => {
	// 	if (debouncedEmail) {
	// 		userService
	// 			.validateEmail(debouncedEmail)
	// 			.then((response) => {
	// 				if (response) {
	// 					const user = response;
	// 					setEmailExists(true);

	// 					for (const key in user) {
	// 						const value = user[key as keyof typeof user];
	// 						if (key === "zipcode") {
	// 							form.setValue(key as keyof DefendantFormSchema, String(value ?? ""));
	// 						} else {
	// 							form.setValue(key as keyof DefendantFormSchema, (value as string) ?? "");
	// 						}
	// 					}
	// 				} else {
	// 					setEmailExists(false);
	// 				}
	// 			})
	// 			.catch((_) => {
	// 				setEmailExists(false);
	// 			});
	// 	}
	// }, [debouncedEmail, form]);

	const countriesQuery = useSuspenseQuery(countriesQueryOptions());

	const countries = countriesQuery.data;
	const regionCode = form.watch("region_code");
	const provinceCode = form.watch("province_code");
	const cityCode = form.watch("city_code");
	const classification = form.watch("classification");
	const firstName = form.watch("first_name");
	const middleName = form.watch("middle_name");
	const lastName = form.watch("last_name");
	const suffix = form.watch("suffix");

	const sameEmail = emails?.includes(form.watch("email") || "");
	const undef = form.watch("email") === "" && emails === "";

	// useEffect(() => {
	// 	form.setValue("email", "");
	// 	form.setValue("region_code", "");
	// 	form.setValue("province_code", "");
	// 	form.setValue("city_code", "");
	// 	form.setValue("first_name", "");
	// 	form.setValue("middle_name", "");
	// 	form.setValue("last_name", "");
	// 	form.setValue("barangay", "");
	// 	form.setValue("address_1", "");
	// 	form.setValue("address_2", "");
	// 	form.setValue("corporation", "");
	// 	setEmailExists(false);
	// }, [classification]);

	useEffect(() => {
		if (!firstName && !middleName && !lastName) {
			setNameExists(false);
			return;
		}

		if (allParties === undefined) {
			return;
		}
		const exists = allParties.some(
			(party: any) =>
				party.first_name?.trim().toUpperCase() === firstName?.trim().toUpperCase() &&
				party.middle_name?.trim().toUpperCase() === middleName?.trim().toUpperCase() &&
				party.last_name?.trim().toUpperCase() === lastName?.trim().toUpperCase() &&
				(party.suffix?.trim().toUpperCase() || "") === (suffix?.trim().toUpperCase() || ""),
		);
		setNameExists(exists);
	}, [allParties, firstName, middleName, lastName, suffix]);

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4">
							{!isEdit && sameEmail && undef && (
								<div className="col-span-12 w-full py-1">
									<Alert type="error" allowClose={false} title="Email Address Already Added as Defendant" icon={<IconAlertCircle size={20} stroke={2} />}>
										This email is already added as {defendantLabel}.
									</Alert>
								</div>
							)}
							{/* {!sameEmail && emailExists && (
								<div className="col-span-12 w-full py-1">
									<Alert type="info" allowClose={false} title="Email Address Already Exists" icon={<IconAlertCircle size={20} stroke={2} />}>
										The system will use the personal information associated with the email address you entered to auto-fill the form fields. If you do not wish
										to use this information, please enter a different email address.
									</Alert>
								</div>
							)} */}
							{nameExists && (
								<div className="col-span-12 w-full py-1">
									<Alert type="warning" allowClose={false} title="Duplicate Name Detected" icon={<IconAlertCircle size={20} stroke={2} />}>
										A party with the same name already exists as a plaintiff or defendant in this case. Please review the entered details to avoid duplicates.
									</Alert>
								</div>
							)}
							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								{caseData !== undefined && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="use_existing_party"
											render={() => (
												<FormItem>
													<CheckboxGroup>
														<FormItem>
															<FormGroup>
																<div className="flex flex-row w-full gap-2">
																	<FormControl>
																		<Checkbox
																			size="lg"
																			name="use_existing_party"
																			checked={existingParty}
																			onCheckedChange={(value) => {
																				setExistingParty(value);
																			}}
																		/>
																	</FormControl>
																	<FormLabel>
																		<div className="flex flex-col w-full">
																			<span className="text-base text-foreground">Use Existing Party?</span>
																		</div>
																	</FormLabel>
																</div>
															</FormGroup>
															<FormMessage />
														</FormItem>
													</CheckboxGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
								{existingParty && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="party_id"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Existing Party</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															defaultValue={field.value}
															returnFullOption
															onValueChange={(values) => {
																field.onChange(values.value);
																form.setValue("party_id", values.data?.id);
																form.setValue("email", values.data?.email);
																form.setValue("first_name", values.data?.user?.first_name);
																form.setValue("last_name", values.data?.user?.last_name);
																form.setValue("middle_name", values.data?.user?.middle_name);
																form.setValue("mobile_number", values.data?.user?.mobile_number);
																form.setValue("classification", values.data?.user?.corporation ? "Juridical" : "Individual");
																form.setValue("corporation", values.data?.user?.corporation);
																form.setValue("barangay", values.data?.barangay);
																form.setValue("address_1", values.data?.address_1 || values.data?.address_2);
																form.setValue("address_2", values.data?.address_2);
																form.setValue("zipcode", values.data?.zipcode || "");
																form.setValue("region_code", values.data?.region_code);
																form.setValue("province_code", values.data?.province_code);
																form.setValue("city_code", values.data?.city_code);
																form.setValue("region", {
																	name: values.data?.region_code,
																	code: values.data?.region_code,
																});
																form.setValue("province", {
																	name: values.data?.province_code,
																	code: values.data?.province_code,
																});
																form.setValue("city", {
																	name: values.data?.city_code,
																	code: values.data?.city_code,
																});
															}}
															loadOptions={async (value: string) => {
																return caseData.collaborators
																	.filter((c) => c.role === "Plaintiff" || c.role === "Defendant")
																	.map((item) => ({
																		label: `${item.user?.full_name} (${item.role})`,
																		value: item.id,
																		data: item,
																	}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}

								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="classification"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Type</FormLabel>
													<FormDescription>Select type of {defendantLabel}</FormDescription>
												</div>
												<FormGroup>
													<Select
														onValueChange={field.onChange}
														defaultValue={field.value}
														options={[
															{
																value: "Juridical",
																label: "Juridical",
															},
															{
																value: "Individual",
																label: "Individual",
															},
															{
																value: "Government Agency",
																label: "Government Agency",
															},
														]}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								{classification && classification === "Individual" && (
									<>
										<div className="col-span-4 flex">
											<FormField
												control={form.control}
												name="first_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>First Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" disabled={emailExists} {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-4 flex">
											<FormField
												control={form.control}
												name="middle_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Middle Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" disabled={emailExists} {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-3 flex">
											<FormField
												control={form.control}
												name="last_name"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Last Name</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" disabled={emailExists} {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-1 flex">
											<FormField
												control={form.control}
												name="suffix"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Suffix</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" disabled={emailExists} {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}

								{classification && classification === "Juridical" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="corporation"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Name of Juridical Entity</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} disabled={emailExists} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}

								{classification && classification === "Government Agency" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="corporation"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Name of Government Agency</FormLabel>
														<FormDescription />
													</div>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} disabled={emailExists} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}

								<div className="col-span-12 flex">
									{isPlaintiff && classification && (
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormLabel required className="flex flex-row gap-1 items-center">
														Email Address
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Email must be valid and active as it will be used for the following:</div>
																	<div className="text-foreground-dark/70 font-medium">
																		• To automatically create an account in our system, unless it's already taken.
																	</div>
																	<div className="text-foreground-dark/70 font-medium">• To set or update an account password.</div>
																	<div className="text-foreground-dark/70 font-medium">• To receive important updates and notifications.</div>
																</div>
															}
															side="top"
														>
															<IconHelpCircle stroke={2} size={20} className="text-primary-600/90" />
														</Tooltip>
													</FormLabel>
													<FormGroup iconPosition="left">
														<Input {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}
								</div>

								<div className="col-span-6 flex">
									<FormField
										control={form.control}
										name="mobile_number"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel>Mobile Number</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<FormPrepend>+639</FormPrepend>
													<Input
														{...field}
														autoComplete="off"
														placeholder=""
														maxLength={9}
														onChange={(e) => {
															form.trigger("mobile_number");
															const onlyNumbers = e.target.value.replace(/\D/g, "");
															field.onChange(onlyNumbers);
														}}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-6 flex">
									<FormField
										control={form.control}
										name="landline"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel>Landline Number</FormLabel>
												</div>
												<FormGroup iconPosition="left">
													<Input
														{...field}
														autoComplete="off"
														placeholder=""
														maxLength={9}
														onChange={(e) => {
															form.trigger("landline");
															const onlyNumbers = e.target.value.replace(/\D/g, "");
															field.onChange(onlyNumbers);
														}}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 flex items-center text-[14px] text-gray-500">
									<div className="flex flex-col w-full">
										<span className="text-lg font-semibold text-foreground-dark">{defendantLabel ? defendantLabel : "Defendant"} Address</span>
										<span className="text-base text-foreground/70">
											Fill in the full address details of the {defendantLabel} to ensure that legal documents are properly served.
										</span>
									</div>
								</div>

								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="address_type"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Address Type</FormLabel>
													<FormDescription>Select type of address</FormDescription>
												</div>
												<FormGroup>
													<Select
														onValueChange={(value) => {
															field.onChange(value);
															form.setValue("address_1", "");
															form.setValue("address_2", "");
															form.setValue("barangay", "");
															form.setValue("region_code", "");
															form.setValue("province_code", "");
															form.setValue("city_code", "");
															form.setValue("province", {});
															form.setValue("city", {});
														}}
														defaultValue={field.value}
														options={[
															{
																value: "domestic",
																label: "Domestic",
															},
															{
																value: "international",
																label: "International",
															},
														]}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								{addressType === "domestic" && (
									<>
										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="region_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Region</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																returnFullOption
																onValueChange={(value) => {
																	form.setValue("province_code", "");
																	form.setValue("city_code", "");
																	form.setValue("province", {});
																	form.setValue("city", {});

																	if (value) {
																		field.onChange(value.value!);
																		form.setValue("region", {
																			name: value.label,
																			code: value.value,
																		});
																	}
																}}
																placeholderValue={{
																	label: data?.region ? data?.region?.name : regionCode,
																	value: regionCode,
																}}
																loadOptions={async (value: string) => {
																	const items = await regionService.list({
																		"filter[search]": value,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="province_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Province</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																disabled={!regionCode}
																returnFullOption
																key={regionCode}
																onValueChange={(value) => {
																	form.setValue("city_code", "");

																	if (value) {
																		field.onChange(value.value);
																		form.setValue("province", {
																			name: value.label,
																			code: value.value,
																			region_code: regionCode,
																		});
																	}
																}}
																placeholderValue={{
																	label: data?.province ? data?.province?.name : provinceCode,
																	value: provinceCode,
																}}
																loadOptions={async (value: string) => {
																	const items = await provinceService.list({
																		"filter[search]": value,
																		"filter[region_code]": regionCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="city_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>City</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																disabled={!provinceCode}
																key={provinceCode}
																returnFullOption
																onValueChange={(value) => {
																	if (value) {
																		field.onChange(value.value);
																		form.setValue("city", {
																			name: value.label,
																			code: value.value,
																			province_code: provinceCode,
																		});
																	}
																}}
																placeholderValue={{
																	label: data?.city ? data?.city?.name : cityCode,
																	value: cityCode,
																}}
																loadOptions={async (value: string) => {
																	const items = await cityService.list({
																		"filter[search]": value,
																		"filter[province_code]": provinceCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="barangay"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Barangay</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="address_1"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Street/Lot No./Building/Room No.</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="address_2"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Apartment/Suite/Unit</FormLabel>
														<FormGroup iconPosition="left">
															<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-6 flex">
											<FormField
												control={form.control}
												name="zipcode"
												render={({ field }) => (
													<FormItem>
														<FormLabel>Zip Code</FormLabel>
														<FormGroup iconPosition="left">
															<NumericFormat
																decimalScale={2}
																thousandsGroupStyle="thousand"
																thousandSeparator={false}
																className={clsx(
																	"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
																	"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
																)}
																{...field}
																onValueChange={(values) => {
																	field.onChange(values.floatValue);
																	// field.onChange(values.floatValue);
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}

								{addressType === "international" && (
									<>
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="country"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Country</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Select
																onValueChange={async (value) => {
																	field.onChange(value);
																}}
																defaultValue={field.value}
																options={countries.map((c) => ({
																	label: c.name,
																	value: c.name,
																}))}
															/>
														</FormGroup>
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="address_1"
												render={({ field }) => (
													<FormItem>
														<FormLabel required>Address</FormLabel>
														<FormGroup iconPosition="left">
															<Input disabled={form.watch("country") === ""} className="uppercase" autoComplete="off" {...field} />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</>
								)}

								<div className="col-span-12 flex items-center text-[14px] text-gray-500">
									<div className="flex flex-col w-full">
										<div className="flex flex-row w-full items-center gap-3">
											<span className="text-lg font-semibold text-foreground-dark">Represented By / Attorney-in-fact</span>
											<Button type="button" onClick={handleAddRow} size="sm" variant="ghost">
												<IconCirclePlus size={16} stroke={2} /> Add
											</Button>
										</div>
										<span className="text-base text-foreground/70">
											If applicable, provide the details of the individual authorized to represent the {defendantLabel}.
										</span>
									</div>
								</div>

								{representatives.fields.map((representative, index) => (
									// <div className="border rounded-md w-full p-4 mt-2" key={representative.id}>
									<div key={representative.id} className="col-span-12 flex flex-col gap-4 border rounded-md p-4">
										<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
											<div className="col-span-12 flex flex-row items-center justify-between">
												<div className="flex flex-col">
													<span className="text-lg font-semibold text-foreground-dark">Representative / Attorney-in-fact #{index + 1}</span>
												</div>
												<Tooltip className="z-[90]" content={<div className="text-foreground-dark/90">Delete</div>} side="top">
													<button
														className="inline-flex justify-center items-center px-3 text-danger-600 hover:text-danger-500"
														type="button"
														onClick={() => representatives.remove(index)}
													>
														<IconTrash size={18} stroke={2} />
													</button>
												</Tooltip>
											</div>

											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name={`representatives[${index}].name`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>Name</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>

											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name={`representatives[${index}].email`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>Email</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>

											<div className="col-span-12 flex">
												<FormField
													control={form.control}
													name={`representatives[${index}].address`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>Address</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
				<div className="flex flex-row justify-between w-full gap-4 mt-4">
					<Button variant="ghost" type="button" onClick={closeModal}>
						Cancel
					</Button>
					{((isPlaintiff && email !== "") || !isPlaintiff) && !nameExists && (
						<Button type="submit" disabled={!isEdit && sameEmail && undef} loading={form.formState.isSubmitting}>
							Confirm
						</Button>
					)}
				</div>
			</form>
		</Form>
	);
}
